import React, { useCallback, useState } from 'react';
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import { Button } from '../../../../../../widgets/buttons';
import { useDialogManager } from '../../../../../../widgets/dialog';
import { showSuccessNotification } from '../../../../../../widgets/notificationToaster';
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
import styled from 'styled-components';
import { pickFiles as pickFileList } from '../pickFiles';

// LE DUE TEXTURE D'AMBIENTE, IN UN POSTO SOLO. La mappa d'ambiente e lo skybox si caricano
// con la stessa procedura: controllo del piano, scelta dei file, validazione — un `.hdr`,
// `.dds`, `.env` oppure sei facce di cubemap con i suffissi giusti — e poi `loadTextureFiles`.
//
// Stava tutta dentro `settingsEditor`, ed è il motivo per cui il pannello World poteva
// mostrare la mappa ma non sostituirla: rifare qui la stessa procedura sarebbe stata la stessa
// procedura in due posti, e le due copie sarebbero divergute alla prima correzione.
//
// Ora la casa è World; le Impostazioni non le mostrano più.

const ErrorBody = styled.div`
    padding: 20px;
    text-align: center;
`;

const UpgradeBanner = styled(UpgradeDialog)`
    margin-bottom: 20px;

    p {
        margin: 0;
        padding: 0;
        line-height: 18px;
    }

    z-index: 10000 !important;
`;

/** Le facce di un cubemap, e l'ordine in cui il motore se le aspetta. */
const CUBEMAP_SUFFIXES = ['px', 'py', 'pz', 'nx', 'ny', 'nz'];

const SINGLE_FILE_EXTENSIONS = ['.hdr', '.dds', '.env', '.basis'];

// Le immagini comuni stanno nell'elenco per le SEI FACCE, che sono `.jpg` o `.png`: da sole non
// passano la validazione qui sotto, e il messaggio dice perché.
const ENVIRONMENT_FILE_TYPES = '.jpeg,.jpg,.png,.hdr,.dds,.env,.basis';

const pickFiles = (onPicked: (files: File[]) => void) => pickFileList(ENVIRONMENT_FILE_TYPES, (files) => onPicked(Array.from(files)));

/**
 * L'INDIRIZZO DI CIÒ CHE È APPENA STATO CARICATO, che è quello che lo slot sa mostrare: da lì
 * legge il nome del file. Il motore espone la texture, non l'indirizzo, e passare l'oggetto di
 * Babylon a chi si aspetta una stringa mandava la pagina in errore. Se l'indirizzo non c'è ancora,
 * il nome del file scelto dice la stessa cosa a chi guarda.
 */
const textureUrl = (texture: any, files: File[]): string | null => {
    const url = texture?.url;
    if (typeof url === 'string' && url) return url;
    return files[0]?.name ?? null;
};

// CARICARE E TOGLIERE SONO MODIFICHE, ma non le vede nessuno. Le modifiche non salvate si contano
// ascoltando `change` e `input` sul pannello — eventi che emette un CAMPO — e questi gesti sono
// pulsanti: si poteva togliere la mappa d'ambiente e uscire senza che niente avvisasse. Chi sa che
// la scena è cambiata è questo hook, ed è lui a dirlo.
export const useEnvironmentTextures = (sceneEditor: any, additionalFeatures: any, onSceneChanged?: () => void) => {
    const { showDialog, closeDialog } = useDialogManager();
    const { sellerMarketplace, updateSubscription } = useSubscriptionData();

    // TRE STATI, NON DUE, perché le domande sono due: `undefined` vuol dire «non l'ho toccata» e
    // lascia parlare la scena salvata; `null` vuol dire «l'ho tolta»; una stringa è l'indirizzo di
    // quella appena caricata. Con due soli stati, togliere una texture rimetteva a schermo quella
    // di prima — «tolta» e «mai toccata» erano lo stesso valore.
    const [environmentTexture, setEnvironmentTexture] = useState<string | null | undefined>(undefined);
    const [skyboxTexture, setSkyboxTexture] = useState<string | null | undefined>(undefined);
    const [isEnvironmentUploading, setIsEnvironmentUploading] = useState(false);
    const [isSkyboxUploading, setIsSkyboxUploading] = useState(false);

    const showError = useCallback(
        (title: string, ...lines: string[]) => {
            showDialog(
                'environment-texture-error',
                <ErrorBody>
                    <h3>{title}</h3>
                    {lines.map((line) => (
                        <p key={line}>{line}</p>
                    ))}
                    <Button onClick={() => closeDialog('environment-texture-error')}>{T._('Close', 'Admin')}</Button>
                </ErrorBody>
            );
        },
        [showDialog, closeDialog]
    );

    /** Il piano copre le texture d'ambiente personalizzate? Se no, si propone l'upgrade. */
    const canCustomize = useCallback(
        (featureName: string) => {
            if (additionalFeatures?.customEnvironmentTexture) return true;

            showDialog(
                'Upgrade_Plan',
                <UpgradeBanner
                    marketPlace={sellerMarketplace ?? undefined}
                    message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), featureName)}
                    onUpgradeCompleted={async () => {
                        updateSubscription();
                    }}
                />
            );
            return false;
        },
        [additionalFeatures, sellerMarketplace, showDialog, updateSubscription]
    );

    /**
     * Un file solo va bene se è `.hdr`, `.dds`, `.env` o `.basis`; sei file vanno bene se
     * sono le sei facce, riconoscibili dal suffisso. Tutto il resto no — e il perché va detto,
     * perché «non funziona» su un'immagine singola sembra un difetto e non una regola.
     */
    const validateFiles = useCallback(
        (files: File[]): boolean => {
            const extension = '.' + (files[0].name.split('.').pop()?.toLowerCase() ?? '');

            if (files.length !== 6 && !SINGLE_FILE_EXTENSIONS.includes(extension)) {
                showError(
                    T._('Unsupported format', 'Admin'),
                    T._('Single image files are not supported as environment textures. Use .hdr, .dds, .env or upload 6 cubemap face images.', 'Admin')
                );
                return false;
            }

            if (files.length === 6) {
                const matched = new Set<string>();
                files.forEach((file) => {
                    const dotIndex = file.name.lastIndexOf('.');
                    if (dotIndex === -1) return;
                    const nameWithoutExtension = file.name.substring(0, dotIndex);
                    const suffix = CUBEMAP_SUFFIXES.find((candidate) => nameWithoutExtension.endsWith('_' + candidate));
                    if (suffix) matched.add(suffix);
                });

                const missing = CUBEMAP_SUFFIXES.filter((suffix) => !matched.has(suffix));
                if (missing.length > 0) {
                    showError(
                        T._('Invalid cubemap files', 'Admin'),
                        T._('Cubemap files must end with _px, _py, _pz, _nx, _ny, _nz before the extension (e.g. sky_px.jpg).', 'Admin'),
                        `Missing: ${missing.map((suffix) => '_' + suffix).join(', ')}`
                    );
                    return false;
                }
            }

            return true;
        },
        [showError]
    );

    const uploadEnvironment = useCallback(() => {
        if (!canCustomize('Grow')) return;

        pickFiles((files) => {
            if (!validateFiles(files)) return;

            setIsEnvironmentUploading(true);
            sceneEditor?.loadTextureFiles(
                files,
                null,
                'environment',
                false,
                () => {
                    setIsEnvironmentUploading(false);
                    // Il motore la monta subito dopo aver risposto: senza il rinvio si
                    // leggerebbe la scena un attimo prima che la texture ci sia.
                    setTimeout(() => setEnvironmentTexture(textureUrl(sceneEditor?.scene?.environmentTexture, files)), 100);
                    onSceneChanged?.();
                    showSuccessNotification({ message: T._('Environment texture updated successfully.', 'Admin') });
                },
                (reason: unknown) => {
                    setIsEnvironmentUploading(false);
                    showError(T._('Upload failed', 'Admin'), typeof reason === 'string' && reason ? reason : T._('Failed uploading texture.', 'Admin'));
                }
            );
        });
    }, [canCustomize, validateFiles, sceneEditor, showError, onSceneChanged]);

    const removeEnvironment = useCallback(() => {
        sceneEditor?.removeSceneEnvironmentTexture();
        setEnvironmentTexture(null);
        onSceneChanged?.();
    }, [sceneEditor, onSceneChanged]);

    const uploadSkybox = useCallback(() => {
        if (!canCustomize('Custom Environment Texture')) return;

        pickFiles((files) => {
            if (!validateFiles(files)) {
                sceneEditor?.setViewerSkyboxCreateSkybox(false);
                return;
            }

            setIsSkyboxUploading(true);
            // Lo skybox si vede solo nel modo viewer: accenderlo qui evita di caricare una
            // texture che poi sembra non essere arrivata.
            sceneEditor?.setEnvironmentMode(2);
            sceneEditor?.setViewerSkyboxCreateSkybox(true);

            sceneEditor?.loadTextureFiles(
                files,
                null,
                'skybox',
                false,
                () => {
                    setIsSkyboxUploading(false);
                    setTimeout(() => setSkyboxTexture(textureUrl(sceneEditor?.getSkyboxTexture?.(), files)), 100);
                    onSceneChanged?.();
                },
                () => {
                    setIsSkyboxUploading(false);
                    showError(T._('Upload failed', 'Admin'), T._('Failed uploading texture.', 'Admin'));
                }
            );
        });
    }, [canCustomize, validateFiles, sceneEditor, showError, onSceneChanged]);

    // TOGLIERE L'IMMAGINE DEL FONDALE NON SPEGNE IL FONDALE: da qui in poi il fondale è l'ambiente,
    // ed è quello che dice l'interruttore cambiando etichetta. Spegnendolo anche nel motore, il
    // pannello e la scena si contraddicevano — l'interruttore restava acceso su un fondale che non
    // c'era più, e riaccenderlo era l'unico modo di rivedere qualcosa.
    const removeSkybox = useCallback(() => {
        sceneEditor?.removeViewerSkyboxTexture();
        setSkyboxTexture(null);
        onSceneChanged?.();
    }, [sceneEditor, onSceneChanged]);

    return {
        environmentTexture,
        setEnvironmentTexture,
        skyboxTexture,
        setSkyboxTexture,
        isEnvironmentUploading,
        isSkyboxUploading,
        uploadEnvironment,
        removeEnvironment,
        uploadSkybox,
        removeSkybox
    };
};
